import { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, GetCallsRequest } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2/call_intelligence.pb';
import { SortType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CallIntelQueries, CallIntelTypes } from '@frontend/api-call-intel';
import { useGlobalPanel } from '@frontend/panel-engine';
import { useAppScopeStore } from '@frontend/scope';
import { defaultPanelContext, useCallTakeawayPanelShallowStore } from './use-call-takeaway-panel-store';

dayjs.extend(utc);

const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';

type OptionParams = {
  defaultAccordion?: CallIntelTypes.CallTakeawayAccordion;
  selectedCallMetaData?: CallIntelTypes.CallMetadata | null;
};

type OpenCallInsightPanelOptions = OptionParams & {
  suppressGlobalPanelOpen?: boolean;
};

export interface CallInsightApi {
  activeSource: CallIntelTypes.PanelSource | null;
  allCalls: CallIntelTypes.Call[];
  defaultAccordion: CallIntelTypes.CallTakeawayAccordion;
  editedCallIds: string[];
  isCallLoading: boolean;
  isDemoAccount?: boolean;
  nextCall: CallIntelTypes.Call | null;
  previousCall: CallIntelTypes.Call | null;
  selectedCall: CallIntelTypes.Call | null;
  selectedCallMetaData: CallIntelTypes.CallMetadata | null;
  selectedDemoTasks: CallIntelTypes.CallTaskListResponse | null;
  source: CallIntelTypes.PanelSource;
  unsavedEditChangesConfig: CallIntelTypes.UnsavedEditChangesConfig;
  close: () => void;
  handleNextClick: () => void;
  handlePreviousClick: () => void;
  open: (call: CallIntelTypes.Call, opts?: OpenCallInsightPanelOptions) => void;
  openById(callId: string, opts?: OpenCallInsightPanelOptions): void;
  setAllCalls: (calls: CallIntelTypes.Call[]) => void;
  setDefaultAccordion: (accordion: CallIntelTypes.CallTakeawayAccordion) => void;
  setEditedCallIds: (callIds: string[]) => void;
  setIsDemoAccount: (isDemoAccount: boolean) => void;
  setSelectedCall: (call: CallIntelTypes.Call | null) => void;
  setSelectedCallMetaData: (callMetaData: CallIntelTypes.CallMetadata | null) => void;
  setSelectedDemoTasks: (tasks: CallIntelTypes.Task[] | null) => void;
  setUnsavedEditChangesConfig: (config: CallIntelTypes.UnsavedEditChangesConfig) => void;
}

type UseCallInsightControllerProps = {
  isDemoAccount?: boolean;
  source: CallIntelTypes.PanelSource;
};

type CallOpenRequest = {
  callId: string;
  opts: OpenCallInsightPanelOptions;
} | null;

export const useCallInsightController = ({ isDemoAccount, source }: UseCallInsightControllerProps): CallInsightApi => {
  const { close: closeGlobal, open: openGlobal } = useGlobalPanel('call-takeaway');
  const { selectedLocationIds } = useAppScopeStore();
  const [callOpenRequest, setCallOpenRequest] = useState<CallOpenRequest>(null);

  const {
    activeSource,
    contexts,
    closeContext,
    handleNextClick: handleNextClickForSource,
    handlePreviousClick: handlePreviousClickForSource,
    openContext,
    setAllCalls: setAllCallsForSource,
    setDefaultAccordion: setDefaultAccordionForSource,
    setEditedCallIds: setEditedCallIdsForSource,
    setIsDemoAccount: setIsDemoAccountForSource,
    setSelectedCall: setSelectedCallForSource,
    setSelectedCallMetaData: setSelectedCallMetaDataForSource,
    setSelectedDemoTasks: setSelectedDemoTasksForSource,
    setUnsavedEditChangesConfig: setUnsavedEditChangesConfigForSource,
  } = useCallTakeawayPanelShallowStore(
    'activeSource',
    'closeContext',
    'contexts',
    'handleNextClick',
    'handlePreviousClick',
    'openContext',
    'setAllCalls',
    'setDefaultAccordion',
    'setEditedCallIds',
    'setIsDemoAccount',
    'setSelectedCall',
    'setSelectedCallMetaData',
    'setSelectedDemoTasks',
    'setUnsavedEditChangesConfig'
  );

  const context = useMemo(() => contexts[source] ?? defaultPanelContext, [contexts, source]);

  const apiDateRange = useMemo(
    () => ({
      start: dayjs.utc('2024-01-01'),
      end: dayjs.utc(),
    }),
    []
  );

  const callIdToFetch = callOpenRequest?.callId;

  const apiRequest = useMemo<GetCallsRequest>(() => {
    const filterMap: Record<number, Filter> = {
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_ID]: { callId: callIdToFetch ? [callIdToFetch] : [] },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
        dateRange: {
          start: apiDateRange.start.format(API_DATE_FORMAT),
          end: apiDateRange.end.format(API_DATE_FORMAT),
        },
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: {
        locations: selectedLocationIds,
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
        callStatus: Object.keys(CallIntelTypes.CallStatus) as CallIntelTypes.CallStatus[],
      },
    };

    return {
      limit: 1,
      offset: 0,
      sortType: SortType.SORT_DESC,
      filter: filterMap,
    };
  }, [callIdToFetch, apiDateRange.start, apiDateRange.end, selectedLocationIds]);

  const { data, isLoading: isCallLoading } = CallIntelQueries.useListCallsQuery({
    request: apiRequest,
    options: {
      enabled: !!callIdToFetch,
      onError: (err) => console.error('Failed to load call by id', err),
    },
  });

  // Keep isDemoAccount in sync with the store for this source
  useEffect(() => {
    setIsDemoAccountForSource({ isDemoAccount: !!isDemoAccount, source });
  }, [isDemoAccount, setIsDemoAccountForSource, source]);

  useEffect(() => {
    if (!callOpenRequest) return;
    const page = data?.pages?.[0];
    const call = page?.calls?.[0] as CallIntelTypes.Call;
    if (call) {
      openContext({ source, call, opts: callOpenRequest.opts });
      if (!callOpenRequest.opts?.suppressGlobalPanelOpen) {
        openGlobal();
      }
      setCallOpenRequest(null);
    }
  }, [callOpenRequest, data?.pages, openContext, openGlobal, source]);

  // Action wrappers

  /**
   * Close the call insight panel.
   */
  const close = useCallback(() => {
    closeContext();
    closeGlobal();
  }, [closeContext, closeGlobal]);

  /**
   * Navigate to the next call in the current source.
   */
  const handleNextClick = useCallback(() => {
    handleNextClickForSource({ source });
  }, [handleNextClickForSource, source]);

  /**
   * Navigate to the previous call in the current source.
   */
  const handlePreviousClick = useCallback(() => {
    handlePreviousClickForSource({ source });
  }, [handlePreviousClickForSource, source]);

  /**
   * Open the call insight panel for a specific call.
   */
  const open = useCallback(
    (call: CallIntelTypes.Call, opts?: OpenCallInsightPanelOptions) => {
      openContext({ call, opts, source });
      if (!opts?.suppressGlobalPanelOpen) {
        openGlobal();
      }
    },
    [openContext, openGlobal, source]
  );

  /** Open the call insight panel by call ID */
  const openById = useCallback((callId: string, opts: OpenCallInsightPanelOptions = {}) => {
    setCallOpenRequest({ callId, opts });
  }, []);

  /**
   * Set all calls for the current source.
   */
  const setAllCalls = useCallback(
    (calls: CallIntelTypes.Call[]) => setAllCallsForSource({ calls, source }),
    [setAllCallsForSource, source]
  );

  /**
   * Set the default accordion for the current source.
   */
  const setDefaultAccordion = useCallback(
    (accordion: CallIntelTypes.CallTakeawayAccordion) => setDefaultAccordionForSource({ accordion, source }),
    [setDefaultAccordionForSource, source]
  );

  /**
   * Set the edited call IDs for the current source.
   */
  const setEditedCallIds = useCallback(
    (callIds: string[]) => setEditedCallIdsForSource({ callIds, source }),
    [setEditedCallIdsForSource, source]
  );

  /**
   * Set whether the current source is a demo account.
   */
  const setIsDemoAccount = useCallback(
    (isDemoAccount: boolean) => setIsDemoAccountForSource({ isDemoAccount, source }),
    [setIsDemoAccountForSource, source]
  );

  /**
   * Set the selected call for the current source.
   */
  const setSelectedCall = useCallback(
    (call: CallIntelTypes.Call | null) => setSelectedCallForSource({ call, source }),
    [setSelectedCallForSource, source]
  );

  /**
   * Set the selected call metadata for the current source.
   */
  const setSelectedCallMetaData = useCallback(
    (callMetaData: CallIntelTypes.CallMetadata | null) => setSelectedCallMetaDataForSource({ callMetaData, source }),
    [setSelectedCallMetaDataForSource, source]
  );

  /**
   * Set the selected demo tasks for the current source.
   */
  const setSelectedDemoTasks = useCallback(
    (tasks: CallIntelTypes.Task[] | null) => setSelectedDemoTasksForSource({ tasks, source }),
    [setSelectedDemoTasksForSource, source]
  );

  /**
   * Set the unsaved edit changes config for the current source.
   */
  const setUnsavedEditChangesConfig = useCallback(
    (config: CallIntelTypes.UnsavedEditChangesConfig) => setUnsavedEditChangesConfigForSource({ config, source }),
    [setUnsavedEditChangesConfigForSource, source]
  );

  return {
    activeSource,
    allCalls: context.allCalls,
    defaultAccordion: context.defaultAccordion,
    editedCallIds: context.editedCallIds,
    isCallLoading,
    isDemoAccount: context.isDemoAccount,
    nextCall: context.nextCall,
    previousCall: context.previousCall,
    selectedCall: context.selectedCall,
    selectedCallMetaData: context.selectedCallMetaData,
    selectedDemoTasks: context.selectedDemoTasks,
    source,
    unsavedEditChangesConfig: context.unsavedEditChangesConfig,
    close,
    handleNextClick,
    handlePreviousClick,
    open,
    openById,
    setAllCalls,
    setDefaultAccordion,
    setEditedCallIds,
    setIsDemoAccount,
    setSelectedCall,
    setSelectedCallMetaData,
    setSelectedDemoTasks,
    setUnsavedEditChangesConfig,
  };
};
